class MtFastProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('.mt-fast-product__form');
      this.variantInput = this.querySelector('[data-mt-fp-variant-id]');
      if (this.variantInput) this.variantInput.name = 'id';
      this.nativeVariantSelect = this.querySelector('[data-mt-fp-native-variant]');
      if (this.nativeVariantSelect) this.nativeVariantSelect.disabled = true;
      this.optionFieldsCache = Array.from(this.querySelectorAll('[data-mt-fp-option-fieldset]'));
      this.variants = JSON.parse(this.querySelector('[data-mt-fp-variants]')?.textContent || '[]');
      this.addButton = this.querySelector('[data-mt-fp-add]');
      this.status = this.querySelector('[data-mt-fp-status]');
      this.mediaTrack = this.querySelector('[data-mt-fp-media-track]');
      this.cartRequestInFlight = false;
      this.currentVariant = null;
      this.dialogFocusOrigins = new WeakMap();
      this.reviewPreviewObserver = null;
      this.reviewPreviewFallbackTimer = null;
      this.reviewPreviewTrigger = null;
      this.informationPanelTimers = new Map();
      this.informationPanelLayoutAnimations = new Set();
      this.specificationPreviewObjectUrls = new Set();
      this.specificationPreviewCleanupTimers = new Set();
      this.zoomMobileQuery = window.matchMedia('(max-width: 767px)');
      this.activeMediaId = String(this.querySelector('[data-mt-fp-media-id].is-active')?.dataset.mtFpMediaId || '');
      this.activeMediaGroup = this.querySelector('[data-mt-fp-media-id].is-active')?.dataset.mtFpMediaGroup || '';
      this.onChangeBound = (event) => {
        if (event.target.matches('[data-mt-fp-option]')) this.syncVariant();
        if (event.target.matches('[data-mt-fp-accessory-variant]')) this.selectAccessoryVariant(event.target);
        if (event.target.matches('[data-mt-fp-accessory-quantity-input]')) this.normalizeAccessoryQuantity(event.target);
        if (event.target.matches('[data-mt-fp-quantity-input]')) {
          this.clearActionStatuses();
          this.syncQuantityControls();
        }
      };
      this.onClickBound = (event) => this.onClick(event);
      this.onKeydownBound = (event) => this.onMaterialKeydown(event);
      this.onSubmitBound = (event) => this.onSubmit(event);
      this.onMediaScrollBound = () => this.scheduleMediaSync();
      this.onZoomViewportChangeBound = () => this.syncZoomAvailability();
      this.onDialogCloseBound = (event) => this.onDialogClose(event.currentTarget);
      this.addEventListener('change', this.onChangeBound);
      this.addEventListener('click', this.onClickBound);
      this.addEventListener('keydown', this.onKeydownBound);
      this.form?.addEventListener('submit', this.onSubmitBound);
      this.querySelectorAll('[data-mt-fp-dialog]').forEach((dialog) => {
        dialog.addEventListener('close', this.onDialogCloseBound);
      });
      this.mediaTrack?.addEventListener('scroll', this.onMediaScrollBound, { passive: true });
      this.zoomMobileQuery.addEventListener?.('change', this.onZoomViewportChangeBound);
      this.setMediaGroup(this.activeMediaGroup);
      this.setupThumbnailHydration();
      this.syncFamilySelection();
      this.resolveInheritedVariant();
      const selected = this.updateOptionAvailability();
      this.syncVariant({ selectedOptions: selected, replaceUrl: false });
      this.syncZoomAvailability();
      this.setupDynamicCheckoutProxy();
      this.setupStickyBar();
      this.setupAccessories();
    }

    selectedOptions() {
      return this.optionFields().map((field) => field.querySelector('[data-mt-fp-option]:checked')?.value || '');
    }

    optionFields() {
      return this.optionFieldsCache || [];
    }

    fieldByRole(role) {
      return this.optionFields().find((field) => field.dataset.mtFpOptionRole === role) || null;
    }

    clearActionStatuses() {
      this.setStatus('', false);
      this.setModalMaterialStatus('');
      this.querySelectorAll('[data-mt-fp-accessory-status]').forEach((status) => {
        status.textContent = '';
        status.hidden = true;
        status.classList.remove('is-error');
      });
    }

    syncQuantityRule(variant) {
      const input = this.querySelector('[data-mt-fp-quantity-input]');
      if (!input) return;
      const rule = variant?.quantity_rule || {};
      const minimum = Math.max(1, Number(rule.min) || 1);
      const increment = Math.max(1, Number(rule.increment) || 1);
      const maximum = Number.isFinite(Number(rule.max)) && Number(rule.max) >= minimum ? Number(rule.max) : null;
      input.min = String(minimum);
      input.step = String(increment);
      if (maximum === null) input.removeAttribute('max');
      else input.max = String(maximum);
      const entered = Number(input.value);
      const normalized = Number.isFinite(entered)
        ? minimum + Math.round((Math.max(minimum, entered) - minimum) / increment) * increment
        : minimum;
      input.value = String(maximum === null ? normalized : Math.min(maximum, normalized));
      this.syncQuantityControls();
    }

    syncQuantityControls() {
      const input = this.querySelector('[data-mt-fp-quantity-input]');
      if (!input) return;
      const value = Number(input.value);
      const minimum = Number(input.min) || 1;
      const maximum = input.max === '' ? null : Number(input.max);
      this.querySelectorAll('[data-mt-fp-quantity="decrease"]').forEach((button) => {
        button.disabled = !Number.isFinite(value) || value <= minimum;
      });
      this.querySelectorAll('[data-mt-fp-quantity="increase"]').forEach((button) => {
        button.disabled = maximum !== null && Number.isFinite(maximum) && value >= maximum;
      });
    }

    nativeDynamicCheckoutTarget() {
      const container = this.querySelector('.mt-fast-product__dynamic-checkout');
      if (!container) return null;
      const selectors = [
        '.shopify-payment-button__button:not([disabled])',
        '[data-shopify-buy-it-now-button]:not([disabled])',
        '.shopify-payment-button__more-options:not([disabled])',
      ];
      return (
        selectors
          .map((selector) => container.querySelector(selector))
          .find((target) => target && target.getClientRects().length > 0) || null
      );
    }

    setupDynamicCheckoutProxy() {
      const container = this.querySelector('.mt-fast-product__dynamic-checkout');
      if (!container) {
        this.syncDynamicCheckoutAvailability();
        return;
      }
      this.dynamicCheckoutObserver?.disconnect();
      if ('MutationObserver' in window) {
        this.dynamicCheckoutObserver = new MutationObserver(() => this.syncDynamicCheckoutAvailability());
        this.dynamicCheckoutObserver.observe(container, { attributes: true, childList: true, subtree: true });
      }
      this.syncDynamicCheckoutAvailability();
    }

    syncDynamicCheckoutAvailability() {
      const target = this.nativeDynamicCheckoutTarget();
      this.dynamicCheckoutTarget = target;
      this.querySelectorAll('[data-mt-fp-sticky-buy-now]').forEach((button) => {
        const visible = Boolean(target && this.currentVariantAvailable);
        const enabled = visible && !this.cartRequestInFlight;
        button.hidden = !visible;
        button.disabled = !enabled;
      });
    }

    syncVariant({ selectedOptions = null, replaceUrl = true } = {}) {
      const selected = selectedOptions || this.updateOptionAvailability();
      this.querySelectorAll('[data-mt-fp-selected-option]').forEach((node, index) => {
        node.textContent = selected[index] || '';
      });
      const variant = this.optionFields().length
        ? this.variants.find((candidate) => JSON.stringify(candidate.options) === JSON.stringify(selected))
        : this.variants.find((candidate) => String(candidate.id) === String(this.variantInput?.value));
      this.syncPurchaseMaterialMaps(selected);
      if (!variant) {
        this.setUnavailable();
        this.syncAccessories(null);
        this.syncPromotionSaving(null);
        this.updateSaleBadge(0);
        return;
      }
      this.currentVariant = variant;
      this.syncPaymentIntegrations(variant);
      this.setAvailability(variant.available);
      this.syncQuantityRule(variant);
      this.querySelectorAll('[data-mt-fp-price], [data-mt-fp-sticky-price]').forEach((node) => {
        if (variant.price_html) node.innerHTML = variant.price_html;
      });
      this.updateStickyMedia(variant);
      this.updateSaleBadge(variant.sale_percent);
      this.syncPromotionSaving(variant);
      this.updateVariantMetadata(variant);
      this.syncAccessories(variant);
      if (variant.featured_media_id) {
        const featuredSlide = Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).find(
          (slide) => String(slide.dataset.mtFpMediaId) === String(variant.featured_media_id)
        );
        if (featuredSlide?.dataset.mtFpMediaGroup && featuredSlide.dataset.mtFpMediaGroup !== this.activeMediaGroup)
          this.setMediaGroup(featuredSlide.dataset.mtFpMediaGroup);
        this.showMedia(String(variant.featured_media_id));
      }
      if (replaceUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
        this.clearActionStatuses();
      }
    }

    syncPaymentIntegrations(variant) {
      const variantId = variant?.id ? String(variant.id) : '';
      const hasVariant = Boolean(variantId);
      if (this.variantInput) {
        this.variantInput.disabled = !hasVariant;
        if (hasVariant) {
          this.variantInput.value = variantId;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.querySelectorAll('[data-mt-fp-payment-terms]').forEach((container) => {
        container.hidden = !hasVariant;
      });
      this.querySelectorAll('[data-mt-fp-payment-terms-variant]').forEach((input) => {
        input.disabled = !hasVariant;
        if (hasVariant) {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    updateOptionAvailability() {
      const selected = [];
      const fields = this.optionFields();
      if (!fields.length) {
        return this.variants.find((variant) => String(variant.id) === String(this.variantInput?.value))?.options || [];
      }
      fields.forEach((field, index) => {
        field.querySelectorAll('[data-mt-fp-option]').forEach((input) => {
          const possible = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              variant.options.slice(0, index).every((value, previousIndex) => value === selected[previousIndex])
          );
          input.disabled = !possible;
          input.setAttribute('aria-disabled', String(!possible));
        });
        let checked = field.querySelector('[data-mt-fp-option]:checked');
        if (!checked || checked.disabled) {
          checked = field.querySelector('[data-mt-fp-option]:not(:disabled)');
          if (checked) checked.checked = true;
        }
        selected[index] = checked?.value || '';
      });
      return selected;
    }

    syncPurchaseMaterialMaps(selected) {
      const fields = this.optionFields();
      const fabricIndex = fields.indexOf(this.fieldByRole('fabric'));
      const colorIndex = fields.indexOf(this.fieldByRole('color'));
      this.querySelectorAll('[data-mt-fp-purchase-material-map]').forEach((button) => {
        const optionValue = button.dataset.mtFpMapOptionValue || '';
        const fabricValue = button.dataset.mtFpMapFabricValue || '';
        const hasAllFields = optionValue && fabricValue && fabricIndex >= 0 && colorIndex >= 0;
        const candidates = hasAllFields
          ? this.variants.filter(
              (variant) => variant.options[fabricIndex] === fabricValue && variant.options[colorIndex] === optionValue
            )
          : [];
        const available = candidates.some((variant) => variant.available);
        const active = hasAllFields && selected[fabricIndex] === fabricValue && selected[colorIndex] === optionValue;
        button.disabled = !available;
        button.classList.toggle('is-active', active);
        if (button.getAttribute('role') === 'radio') {
          button.setAttribute('aria-checked', String(active));
          button.tabIndex = active ? 0 : -1;
        } else button.setAttribute('aria-pressed', String(active));
        if (active && button.dataset.mtFpMaterialLabel)
          this.querySelectorAll('[data-mt-fp-material-selection]').forEach((node) => {
            node.textContent = button.dataset.mtFpMaterialLabel;
          });
      });
    }

    updateSaleBadge(percent) {
      const badge = this.querySelector('[data-mt-fp-sale-badge]');
      if (!badge) return;
      const value = Number(percent) || 0;
      badge.hidden = value <= 0;
      if (value > 0) badge.textContent = (this.dataset.saveTemplate || '').replace('__percent__', String(value));
    }

    syncPromotionSaving(variant) {
      const savingText = variant?.promotion_saving_text || '';
      this.querySelectorAll('[data-mt-fp-promotion-saving]').forEach((node) => {
        node.textContent = savingText;
        node.hidden = !savingText;
      });
    }

    updateVariantMetadata(variant) {
      this.querySelectorAll('[data-mt-fp-sku]').forEach((node) => {
        node.textContent = variant.sku || '';
      });
      this.querySelectorAll('[data-mt-fp-sku-row]').forEach((row) => {
        row.hidden = !variant.sku;
      });
      this.querySelectorAll('[data-mt-fp-inventory]').forEach((node) => {
        node.textContent = variant.available ? this.dataset.inStockLabel : this.dataset.outOfStockLabel;
      });
    }

    updateStickyMedia(variant) {
      const media = this.querySelector('[data-mt-fp-sticky-media]');
      const image = media?.querySelector('.mt-fast-product__sticky-image');
      if (!media || !image || !variant.sticky_media_src || media.dataset.mtFpStickyVariantId === String(variant.id))
        return;
      if (variant.sticky_media_srcset) image.srcset = variant.sticky_media_srcset;
      image.sizes = '56px';
      image.src = variant.sticky_media_src;
      media.dataset.mtFpStickyVariantId = String(variant.id);
    }

    setAvailability(available) {
      const label = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      this.currentVariantAvailable = Boolean(available);
      if (this.addButton) this.addButton.disabled = !available || this.cartRequestInFlight;
      this.querySelectorAll('[data-mt-fp-add-label], [data-mt-fp-sticky-label]').forEach((node) => {
        node.textContent = label;
      });
      this.querySelectorAll('[data-mt-fp-sticky-add]').forEach((button) => {
        button.disabled = !available || this.cartRequestInFlight;
      });
      this.syncDynamicCheckoutAvailability();
    }

    setUnavailable() {
      this.currentVariant = null;
      this.currentVariantAvailable = false;
      this.syncPaymentIntegrations(null);
      if (this.addButton) this.addButton.disabled = true;
      this.querySelectorAll('[data-mt-fp-add-label], [data-mt-fp-sticky-label]').forEach((node) => {
        node.textContent = this.dataset.unavailableLabel;
      });
      this.querySelectorAll('[data-mt-fp-sticky-add]').forEach((button) => {
        button.disabled = true;
      });
      this.syncDynamicCheckoutAvailability();
    }

    informationPanelBlocks() {
      const information = this.querySelector('.mt-fast-product__information');
      if (!information) return [];
      return Array.from(information.children).filter((node) =>
        node.matches?.('.mt-fast-product__block, .mt-fast-product__selection-panel-group')
      );
    }

    captureInformationPanelRects() {
      return new Map(this.informationPanelBlocks().map((node) => [node, node.getBoundingClientRect()]));
    }

    finishInformationPanelLayoutAnimations() {
      this.informationPanelLayoutAnimations?.forEach((animation) => {
        try {
          animation.finish();
        } catch (_error) {
          animation.cancel();
        }
      });
      this.informationPanelLayoutAnimations?.clear();
    }

    animateInformationPanelLayoutFrom(beforeRects) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof Element.prototype.animate !== 'function') return;
      const duration = window.matchMedia('(max-width: 767px)').matches ? 200 : 220;
      this.informationPanelBlocks().forEach((node) => {
        const before = beforeRects.get(node);
        if (!before) return;
        const after = node.getBoundingClientRect();
        const deltaY = before.top - after.top;
        if (Math.abs(deltaY) < 0.5) return;
        const animation = node.animate(
          [{ transform: `translateY(${deltaY}px)` }, { transform: 'translateY(0)' }],
          { duration, easing: 'cubic-bezier(.16, 1, .3, 1)' }
        );
        this.informationPanelLayoutAnimations.add(animation);
        animation.finished
          .catch(() => {})
          .finally(() => this.informationPanelLayoutAnimations?.delete(animation));
      });
    }

    clearInformationPanelTimer(panel) {
      const timer = this.informationPanelTimers?.get(panel);
      if (timer) window.clearTimeout(timer);
      this.informationPanelTimers?.delete(panel);
    }

    toggleInformationPanel(panel) {
      if (!(panel instanceof HTMLDetailsElement)) return;
      const transition = panel.dataset.mtFpInformationTransition || '';
      const shouldOpen = transition === 'closing' || !panel.open;
      this.clearInformationPanelTimer(panel);
      this.finishInformationPanelLayoutAnimations();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.open = shouldOpen;
        delete panel.dataset.mtFpInformationTransition;
        return;
      }

      if (shouldOpen) {
        if (!panel.open) {
          const beforeRects = this.captureInformationPanelRects();
          panel.open = true;
          this.animateInformationPanelLayoutFrom(beforeRects);
        }
        panel.dataset.mtFpInformationTransition = 'opening';
        const duration = window.matchMedia('(max-width: 767px)').matches ? 240 : 260;
        const timer = window.setTimeout(() => {
          if (panel.dataset.mtFpInformationTransition === 'opening') {
            delete panel.dataset.mtFpInformationTransition;
          }
          this.informationPanelTimers?.delete(panel);
        }, duration);
        this.informationPanelTimers.set(panel, timer);
        return;
      }

      panel.dataset.mtFpInformationTransition = 'closing';
      const duration = window.matchMedia('(max-width: 767px)').matches ? 120 : 130;
      const timer = window.setTimeout(() => {
        if (panel.dataset.mtFpInformationTransition !== 'closing') return;
        const beforeRects = this.captureInformationPanelRects();
        panel.open = false;
        delete panel.dataset.mtFpInformationTransition;
        this.informationPanelTimers?.delete(panel);
        this.animateInformationPanelLayoutFrom(beforeRects);
      }, duration);
      this.informationPanelTimers.set(panel, timer);
    }

    onClick(event) {
      if (event.target.matches('[data-mt-fp-dialog]')) {
        this.closeDialog(event.target);
        return;
      }
      const specificationFileLink = event.target.closest('[data-mt-fp-specification-file-links] a[href]');
      if (this.isPreviewableSpecificationFile(specificationFileLink)) {
        if (this.openSpecificationFilePreview(specificationFileLink)) event.preventDefault();
        return;
      }
      const informationSummary = event.target.closest('summary');
      const informationPanel = informationSummary?.parentElement;
      if (informationPanel?.matches('[data-mt-fp-information-panel]')) {
        event.preventDefault();
        this.toggleInformationPanel(informationPanel);
        return;
      }
      const mediaFilter = event.target.closest('[data-mt-fp-media-filter]');
      if (mediaFilter) {
        if (this.isReviewPreviewOpen()) this.closeReviewPreview({ restoreFocus: false });
        const changed = this.setMediaGroup(mediaFilter.dataset.mtFpMediaFilter);
        if (changed && mediaFilter.matches('a[href^="#"]')) event.preventDefault();
        return;
      }
      const mediaTarget = event.target.closest('[data-mt-fp-media-target]');
      if (mediaTarget) {
        this.hydrateThumbnail(mediaTarget.closest('.mt-fast-product__thumbnail'));
        // A direct target selection must not animate through every intervening
        // slide. The active dot and thumbnail are committed with the target,
        // while only a user-driven swipe is allowed to update from scroll.
        this.showMedia(mediaTarget.dataset.mtFpMediaTarget, { behavior: 'auto' });
        return;
      }
      const thumbnailScroll = event.target.closest('[data-mt-fp-thumbnail-scroll]');
      if (thumbnailScroll) {
        this.scrollThumbnails(thumbnailScroll.dataset.mtFpThumbnailScroll);
        return;
      }
      const familyProductLink = event.target.closest('[data-mt-fp-family-product-link]');
      if (familyProductLink) {
        if (familyProductLink.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        familyProductLink.href = this.inheritedProductUrl(familyProductLink.href);
        return;
      }
      const quantity = event.target.closest('[data-mt-fp-quantity]');
      if (quantity) {
        this.changeQuantity(quantity.dataset.mtFpQuantity);
        return;
      }
      const reviewPreviewTrigger = event.target.closest('[data-mt-fp-open-review-preview]');
      if (reviewPreviewTrigger) {
        const opened = this.openReviewPreview(reviewPreviewTrigger);
        if (opened && reviewPreviewTrigger.matches('a[href^="#"]')) event.preventDefault();
        return;
      }
      const reviewPreviewMore = event.target.closest('[data-mt-fp-review-preview-more]');
      if (reviewPreviewMore) {
        const reviewAnchor = document.getElementById(this.dataset.reviewAnchorId || '');
        if (!reviewAnchor) return;
        event.preventDefault();
        this.closeReviewPreview({ restoreFocus: false });
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        reviewAnchor.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        reviewAnchor.focus({ preventScroll: true });
        return;
      }
      const dialogTrigger = event.target.closest('[data-mt-fp-open-dialog]');
      if (dialogTrigger) {
        const opened = this.openDialog(dialogTrigger.dataset.mtFpOpenDialog);
        if (opened && dialogTrigger.matches('a[href^="#"]')) event.preventDefault();
        return;
      }
      if (event.target.closest('[data-mt-fp-open-material]')) {
        this.openMaterialDialog();
        return;
      }
      const modalVisual = event.target.closest('[data-mt-fp-modal-visual]');
      if (modalVisual) {
        this.selectModalMaterialVisual(modalVisual);
        return;
      }
      const purchaseMaterialMap = event.target.closest('[data-mt-fp-purchase-material-map]');
      if (purchaseMaterialMap) {
        this.selectPurchaseMaterialMap(purchaseMaterialMap);
        return;
      }
      const modalAdd = event.target.closest('[data-mt-fp-modal-add]');
      if (modalAdd) {
        this.addModalMaterial(modalAdd);
        return;
      }
      const accessoryQuantity = event.target.closest('[data-mt-fp-accessory-quantity]');
      if (accessoryQuantity) {
        this.changeAccessoryQuantity(accessoryQuantity);
        return;
      }
      const accessoryAdd = event.target.closest('[data-mt-fp-accessory-add]');
      if (accessoryAdd) {
        this.addAccessory(accessoryAdd);
        return;
      }
      if (event.target.closest('[data-mt-fp-close-dialog]')) {
        this.closeDialog(event.target.closest('[data-mt-fp-dialog]'));
        return;
      }
      const copy = event.target.closest('[data-mt-fp-copy]');
      if (copy) {
        this.copyText(copy.dataset.mtFpCopy, copy);
        return;
      }
      const share = event.target.closest('[data-mt-fp-share]');
      if (share) {
        this.shareCurrentProduct(share);
        return;
      }
      if (event.target.closest('[data-mt-fp-sticky-add]')) this.submitPrimaryProduct();
      if (event.target.closest('[data-mt-fp-sticky-buy-now]')) this.submitDynamicCheckout();
    }

    onMaterialKeydown(event) {
      if (event.key === 'Escape' && this.isReviewPreviewOpen()) {
        event.preventDefault();
        this.closeReviewPreview();
        return;
      }
      const openDialog = event.target.closest('[data-mt-fp-dialog][open]');
      if (openDialog && event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog(openDialog);
        return;
      }
      if (openDialog && event.key === 'Tab' && openDialog.classList.contains('mt-fast-product__dialog--fallback')) {
        const focusable = Array.from(
          openDialog.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((node) => node.getClientRects().length > 0);
        if (focusable.length) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
        return;
      }
      const modalCurrent = event.target.closest('[data-mt-fp-modal-visual][role="radio"]');
      if (modalCurrent) {
        this.moveMaterialRadio(event, modalCurrent, '[data-mt-fp-modal-visual]', (next) =>
          this.selectModalMaterialVisual(next, { focus: true })
        );
        return;
      }
      const current = event.target.closest('[data-mt-fp-purchase-material-map][role="radio"]');
      if (!current || !['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const radios = Array.from(
        current.closest('[role="radiogroup"]')?.querySelectorAll('[data-mt-fp-purchase-material-map][role="radio"]') ||
          []
      ).filter((button) => !button.disabled);
      if (!radios.length) return;
      event.preventDefault();
      const currentIndex = Math.max(0, radios.indexOf(current));
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % radios.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
        nextIndex = (currentIndex - 1 + radios.length) % radios.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = radios.length - 1;
      radios[nextIndex].focus();
      this.selectPurchaseMaterialMap(radios[nextIndex]);
    }

    moveMaterialRadio(event, current, selector, onSelect) {
      if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const radios = Array.from(current.closest('[role="radiogroup"]')?.querySelectorAll(selector) || []).filter(
        (button) => !button.disabled
      );
      if (!radios.length) return;
      event.preventDefault();
      const currentIndex = Math.max(0, radios.indexOf(current));
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % radios.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
        nextIndex = (currentIndex - 1 + radios.length) % radios.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = radios.length - 1;
      onSelect(radios[nextIndex]);
    }

    setupAccessories() {
      this.syncAccessories(this.currentVariant);
    }

    clearAccessoryStatus(carousel) {
      const status = carousel?.querySelector('[data-mt-fp-accessory-status]');
      if (!status) return;
      status.textContent = '';
      status.hidden = true;
      status.classList.remove('is-error');
    }

    normalizeAccessoryOption(value) {
      return String(value || '')
        .normalize('NFKC')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase();
    }

    accessoryVariants(slide) {
      if (!slide) return [];
      if (slide.mtFpAccessoryVariants) return slide.mtFpAccessoryVariants;
      try {
        slide.mtFpAccessoryVariants = JSON.parse(
          slide.querySelector('[data-mt-fp-accessory-variants]')?.textContent || '[]'
        );
      } catch (_) {
        slide.mtFpAccessoryVariants = [];
      }
      return slide.mtFpAccessoryVariants;
    }

    compatibleAccessorySlides(carousel) {
      return Array.from(carousel?.querySelectorAll('[data-mt-fp-accessory-slide]') || []).filter(
        (slide) => slide.dataset.mtFpAccessoryCompatible === 'true'
      );
    }

    updateAccessorySlide(slide, variant) {
      if (!slide || !variant) return;
      slide.dataset.mtFpAccessoryVariantId = String(variant.id || '');
      slide.dataset.mtFpAccessoryAvailable = String(Boolean(variant.available));
      const link = slide.querySelector('[data-mt-fp-accessory-link]');
      if (link && variant.url) link.href = variant.url;
      const price = slide.querySelector('[data-mt-fp-accessory-price]');
      if (price) price.innerHTML = variant.price_html || '';
      const image = slide.querySelector('.mt-fast-product__accessory-image');
      if (image && variant.image_src) {
        image.src = variant.image_src;
        if (variant.image_srcset) image.srcset = variant.image_srcset;
        else image.removeAttribute('srcset');
      }
      const addButton = slide.querySelector('[data-mt-fp-accessory-add]');
      if (addButton) {
        const available = Boolean(variant.available && variant.id);
        addButton.dataset.mtFpAccessoryAdd = String(variant.id || '');
        addButton.disabled = !available || this.cartRequestInFlight;
        addButton.querySelectorAll('[data-mt-fp-accessory-add-text]').forEach((addButtonText) => {
          addButtonText.textContent = available
            ? addButton.dataset.mtFpAccessoryAddLabel
            : addButton.dataset.mtFpAccessorySoldOutLabel;
        });
      }
      this.normalizeAccessoryQuantity(slide.querySelector('[data-mt-fp-accessory-quantity-input]'));
    }

    populateAccessoryVariantSelect(slide, variants, selectedVariant) {
      const field = slide?.querySelector('[data-mt-fp-accessory-variant-field]');
      const select = field?.querySelector('[data-mt-fp-accessory-variant]');
      if (!field || !select) return;
      const soldOutLabel = slide.closest('[data-mt-fp-accessory-carousel]')?.dataset.mtFpAccessorySoldOutLabel || '';
      const options = variants.map((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = `${variant.label || ''}${variant.available ? '' : ` — ${soldOutLabel}`}`;
        option.selected = String(variant.id) === String(selectedVariant?.id);
        return option;
      });
      select.replaceChildren(...options);
      field.hidden = variants.length < 2;
    }

    syncAccessoryDiagnostic(slide, reason) {
      const key = slide?.dataset.mtFpAccessoryKey;
      const diagnostic = key === undefined ? null : this.querySelector(`[data-mt-fp-accessory-diagnostic="${key}"]`);
      if (!diagnostic) return;
      if (reason === 'missing') diagnostic.textContent = diagnostic.dataset.mtFpAccessoryMissingText || '';
      if (reason === 'sold_out') diagnostic.textContent = diagnostic.dataset.mtFpAccessorySoldOutText || '';
      diagnostic.hidden = !reason;
    }

    syncAccessories(mainVariant) {
      const fields = this.optionFields();
      const fabricIndex = fields.indexOf(this.fieldByRole('fabric'));
      const colorIndex = fields.indexOf(this.fieldByRole('color'));
      const hasSelection = Boolean(mainVariant && fabricIndex >= 0 && colorIndex >= 0);
      const fabricValue = hasSelection ? this.normalizeAccessoryOption(mainVariant.options[fabricIndex]) : '';
      const colorValue = hasSelection ? this.normalizeAccessoryOption(mainVariant.options[colorIndex]) : '';

      this.querySelectorAll('[data-mt-fp-accessory-carousel]').forEach((carousel) => {
        Array.from(carousel.querySelectorAll('[data-mt-fp-accessory-slide]')).forEach((slide) => {
          const variants = this.accessoryVariants(slide);
          const accessoryFabricIndexValue = slide.dataset.mtFpAccessoryFabricIndex || '';
          const accessoryColorIndexValue = slide.dataset.mtFpAccessoryColorIndex || '';
          const accessoryFabricIndex = Number(accessoryFabricIndexValue);
          const accessoryColorIndex = Number(accessoryColorIndexValue);
          const hasAccessoryRoles =
            accessoryFabricIndexValue !== '' &&
            accessoryColorIndexValue !== '' &&
            Number.isInteger(accessoryFabricIndex) &&
            accessoryFabricIndex >= 0 &&
            Number.isInteger(accessoryColorIndex) &&
            accessoryColorIndex >= 0;
          const exactMatches = hasSelection
            ? variants.filter(
                (variant) =>
                  hasAccessoryRoles &&
                  this.normalizeAccessoryOption(variant.options?.[accessoryFabricIndex]) === fabricValue &&
                  this.normalizeAccessoryOption(variant.options?.[accessoryColorIndex]) === colorValue
              )
            : [];
          const aliasCandidates =
            exactMatches.length === 0 && hasSelection && mainVariant?.accessory_match_key
              ? variants.filter((variant) => variant.match_key === mainVariant.accessory_match_key)
              : [];
          const aliasColors = new Set(
            aliasCandidates.map((variant) => this.normalizeAccessoryOption(variant.options?.[accessoryColorIndex]))
          );
          const aliasMatches = aliasColors.size <= 1 ? aliasCandidates : [];
          const rawMatches = exactMatches.length > 0 ? exactMatches : aliasMatches;
          slide.dataset.mtFpAccessoryMatchMode =
            exactMatches.length > 0 ? 'exact' : aliasMatches.length > 0 ? 'alias' : '';
          const eligible = rawMatches.filter((variant) => variant.available);
          const currentId = slide.dataset.mtFpAccessoryVariantId || '';
          const selected =
            eligible.find((variant) => String(variant.id) === currentId) ||
            eligible.find((variant) => variant.available) ||
            eligible[0] ||
            null;
          slide.dataset.mtFpAccessoryCompatible = String(Boolean(selected));
          if (selected) {
            this.updateAccessorySlide(slide, selected);
            this.populateAccessoryVariantSelect(slide, eligible, selected);
          } else {
            const field = slide.querySelector('[data-mt-fp-accessory-variant-field]');
            if (field) field.hidden = true;
          }
          const reason = rawMatches.length === 0 ? 'missing' : eligible.length === 0 ? 'sold_out' : '';
          this.syncAccessoryDiagnostic(slide, reason);
        });
        this.setAccessoryVisibility(carousel);
      });
    }

    selectAccessoryVariant(select) {
      const slide = select?.closest('[data-mt-fp-accessory-slide]');
      const variant = this.accessoryVariants(slide).find((candidate) => String(candidate.id) === String(select.value));
      if (!variant || !variant.available) {
        this.syncAccessories(this.currentVariant);
        return;
      }
      this.clearAccessoryStatus(select.closest('[data-mt-fp-accessory-carousel]'));
      this.updateAccessorySlide(slide, variant);
      this.setAccessoryVisibility(select.closest('[data-mt-fp-accessory-carousel]'));
    }

    setAccessoryVisibility(carousel) {
      if (!carousel) return;
      const allSlides = Array.from(carousel.querySelectorAll('[data-mt-fp-accessory-slide]'));
      const slides = this.compatibleAccessorySlides(carousel);
      carousel.hidden = slides.length === 0;
      allSlides.forEach((slide) => {
        slide.hidden = slide.dataset.mtFpAccessoryCompatible !== 'true';
      });
    }

    accessoryQuantityRule(slide) {
      const variantId = slide?.dataset.mtFpAccessoryVariantId || '';
      const variant = this.accessoryVariants(slide).find((candidate) => String(candidate.id) === String(variantId));
      const rule = variant?.quantity_rule || {};
      const min = Math.max(1, Number.parseInt(rule.min, 10) || 1);
      const increment = Math.max(1, Number.parseInt(rule.increment, 10) || 1);
      const maximum = Number.parseInt(rule.max, 10);
      return { min, increment, max: Number.isFinite(maximum) && maximum >= min ? maximum : null };
    }

    normalizeAccessoryQuantity(input) {
      const slide = input?.closest('[data-mt-fp-accessory-slide]');
      if (!input || !slide) return 1;
      const { min, increment, max } = this.accessoryQuantityRule(slide);
      const parsed = Number.parseInt(input.value, 10);
      const requested = Number.isFinite(parsed) ? parsed : min;
      const stepped = min + Math.max(0, Math.round((requested - min) / increment)) * increment;
      const value = max === null ? Math.max(min, stepped) : Math.min(max, Math.max(min, stepped));
      input.min = String(min);
      input.step = String(increment);
      if (max === null) input.removeAttribute('max');
      else input.max = String(max);
      input.value = String(value);
      return value;
    }

    changeAccessoryQuantity(button) {
      const slide = button?.closest('[data-mt-fp-accessory-slide]');
      const input = slide?.querySelector('[data-mt-fp-accessory-quantity-input]');
      if (!input) return;
      const { min, increment, max } = this.accessoryQuantityRule(slide);
      const current = this.normalizeAccessoryQuantity(input);
      const direction = button.dataset.mtFpAccessoryQuantity === 'decrease' ? -1 : 1;
      const requested = current + direction * increment;
      const value = max === null ? Math.max(min, requested) : Math.min(max, Math.max(min, requested));
      input.value = String(value);
      this.clearAccessoryStatus(slide.closest('[data-mt-fp-accessory-carousel]'));
    }

    submitPrimaryProduct() {
      if (!this.form || !this.addButton || this.addButton.disabled) return;
      this.form.requestSubmit(this.addButton);
    }

    submitDynamicCheckout() {
      const target = this.nativeDynamicCheckoutTarget();
      if (!target || !this.currentVariantAvailable || this.cartRequestInFlight) {
        this.dynamicCheckoutTarget = null;
        this.syncDynamicCheckoutAvailability();
        return;
      }
      target.click();
    }

    changeQuantity(direction) {
      const input = this.querySelector('[data-mt-fp-quantity-input]');
      if (!input) return;
      const minimum = Number(input.min) || 1;
      const increment = Number(input.step) || 1;
      const maximum = input.max === '' ? null : Number(input.max);
      const current = Number(input.value);
      let next = Number.isFinite(current) ? current : minimum;
      next = direction === 'increase' ? next + increment : next - increment;
      next = Math.max(minimum, next);
      if (maximum !== null && Number.isFinite(maximum)) next = Math.min(maximum, next);
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    scrollThumbnails(direction) {
      const scroller = this.querySelector('[data-mt-fp-thumbnail-scroller]');
      if (!scroller) return;
      const offset = Math.max(104, Math.round(scroller.clientWidth * 0.8));
      scroller.scrollBy({ left: direction === 'next' ? offset : -offset, behavior: 'smooth' });
    }

    syncMediaFilterState(group, { reviewActive = false } = {}) {
      this.querySelectorAll('[data-mt-fp-media-filter]').forEach((filter) => {
        const active = filter.dataset.mtFpMediaFilter === group;
        filter.classList.toggle('is-active', active);
        filter.classList.toggle('mt-button-motion__embedded-control--primary', active);
        filter.classList.toggle('mt-button-motion__embedded-control--secondary', !active);
        if (active) filter.setAttribute('aria-current', 'true');
        else filter.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-mt-fp-open-review-preview]').forEach((trigger) => {
        trigger.classList.toggle('is-active', reviewActive);
        trigger.classList.toggle('mt-button-motion__embedded-control--primary', reviewActive);
        trigger.classList.toggle('mt-button-motion__embedded-control--secondary', !reviewActive);
        if (reviewActive) {
          trigger.setAttribute('aria-current', 'true');
          trigger.setAttribute('aria-expanded', 'true');
        } else {
          trigger.removeAttribute('aria-current');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    }

    setMediaGroup(group) {
      if (!group) return false;
      const visibleSlides = Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).filter(
        (slide) => slide.dataset.mtFpMediaGroup === group
      );
      if (!visibleSlides.length) return false;
      this.activeMediaGroup = group;
      this.querySelectorAll('[data-mt-fp-media-id]').forEach((slide) => {
        const visible = slide.dataset.mtFpMediaGroup === group;
        slide.hidden = !visible;
        if (!visible) {
          slide.classList.remove('is-active');
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
      this.querySelectorAll('[data-mt-fp-media-target]').forEach((target) => {
        const visible = target.dataset.mtFpMediaGroup === group;
        target.hidden = !visible;
        if (!visible) {
          target.classList.remove('is-active');
          target.setAttribute('aria-pressed', 'false');
        }
      });
      this.syncMediaFilterState(group);
      this.mediaTrack?.scrollTo({ left: 0, behavior: 'auto' });
      this.querySelector('[data-mt-fp-thumbnail-scroller]')?.scrollTo({ left: 0, behavior: 'auto' });
      const preferredSlide =
        visibleSlides.find((slide) => String(slide.dataset.mtFpMediaId) === String(this.activeMediaId)) ||
        visibleSlides[0];
      this.showMedia(preferredSlide.dataset.mtFpMediaId, { behavior: 'auto' });
      return true;
    }

    showMedia(id, { behavior = 'auto' } = {}) {
      if (!id) return;
      if (this.isReviewPreviewOpen()) this.closeReviewPreview({ restoreFocus: false });
      const visibleSlides = Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).filter((slide) => !slide.hidden);
      const targetSlide = visibleSlides.find((slide) => String(slide.dataset.mtFpMediaId) === String(id));
      if (!targetSlide) return;
      this.activeMediaId = String(id);
      this.querySelectorAll('[data-mt-fp-media-id]').forEach((slide) => {
        const active = !slide.hidden && String(slide.dataset.mtFpMediaId) === this.activeMediaId;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
        if (active && this.mediaTrack && window.matchMedia('(max-width: 767px)').matches)
          this.mediaTrack.scrollTo({ left: slide.offsetLeft, behavior });
      });
      this.querySelectorAll('[data-mt-fp-media-target]').forEach((button) => {
        const active = !button.hidden && String(button.dataset.mtFpMediaTarget) === this.activeMediaId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.syncActiveThumbnail({ behavior });
      this.syncZoomAvailability();
    }

    scheduleMediaSync() {
      if (this.mediaSyncFrame) return;
      this.mediaSyncFrame = window.requestAnimationFrame(() => {
        this.mediaSyncFrame = null;
        if (!this.mediaTrack || window.matchMedia('(min-width: 768px)').matches) return;
        const slides = Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).filter((slide) => !slide.hidden);
        const index = Math.max(
          0,
          Math.min(slides.length - 1, Math.round(this.mediaTrack.scrollLeft / Math.max(1, this.mediaTrack.clientWidth)))
        );
        const slide = slides[index];
        if (!slide || String(slide.dataset.mtFpMediaId) === this.activeMediaId) return;
        this.activeMediaId = String(slide.dataset.mtFpMediaId);
        slides.forEach((item) => {
          const active = item === slide;
          item.classList.toggle('is-active', active);
          if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
        });
        this.querySelectorAll('[data-mt-fp-media-target]').forEach((button) => {
          const active = !button.hidden && String(button.dataset.mtFpMediaTarget) === this.activeMediaId;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        this.syncActiveThumbnail({ behavior: 'auto' });
        this.syncZoomAvailability();
      });
    }

    syncActiveThumbnail({ behavior = 'auto' } = {}) {
      const scroller = this.querySelector('[data-mt-fp-thumbnail-scroller]');
      if (!scroller) return;
      const thumbnail = Array.from(scroller.querySelectorAll('[data-mt-fp-media-target]')).find((item) => {
        return !item.hidden && String(item.dataset.mtFpMediaTarget) === this.activeMediaId;
      });
      if (!thumbnail) return;
      this.hydrateThumbnail(thumbnail);
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const snapInset = Number.parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 0;
      const targetLeft = isMobile
        ? Math.max(0, thumbnail.offsetLeft - snapInset)
        : Math.max(0, thumbnail.offsetLeft - (scroller.clientWidth - thumbnail.clientWidth) / 2);
      scroller.scrollTo({ left: targetLeft, behavior });
    }

    syncZoomAvailability() {
      const zoom = this.querySelector('[data-mt-fp-open-dialog="zoom"]');
      const activeSlide = Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).find(
        (slide) => !slide.hidden && String(slide.dataset.mtFpMediaId) === this.activeMediaId
      );
      if (!zoom || !activeSlide) return;
      const hasZoomableMedia = this.zoomableMediaSlides().length > 0;
      const isMobile = this.zoomMobileQuery?.matches ?? window.matchMedia('(max-width: 767px)').matches;
      if (isMobile) {
        zoom.hidden = !hasZoomableMedia;
        return;
      }
      const activeSlideIsZoomable = Boolean(this.zoomableMediaSlide(activeSlide));
      zoom.hidden = !activeSlideIsZoomable;
      zoom.toggleAttribute('data-mt-fp-mobile-zoom-fallback', !activeSlideIsZoomable);
    }

    zoomableMediaSlide(slide) {
      if (slide?.dataset.mtFpMediaType !== 'image') return null;
      const media = slide.querySelector('picture, .mt-fast-product__media-image');
      const image = media?.matches('img') ? media : media?.querySelector('img');
      return image ? slide : null;
    }

    zoomableMediaSlides() {
      return Array.from(this.querySelectorAll('[data-mt-fp-media-id]')).filter((slide) => this.zoomableMediaSlide(slide));
    }

    resolveZoomMediaId(requestedMediaId = this.activeMediaId) {
      const slides = Array.from(this.querySelectorAll('[data-mt-fp-media-id]'));
      const requestedIndex = slides.findIndex(
        (slide) => String(slide.dataset.mtFpMediaId) === String(requestedMediaId || '')
      );
      const requestedSlide = requestedIndex >= 0 ? slides[requestedIndex] : null;
      if (this.zoomableMediaSlide(requestedSlide)) return String(requestedSlide.dataset.mtFpMediaId);

      const isMobile = this.zoomMobileQuery?.matches ?? window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) return '';

      const zoomableSlides = this.zoomableMediaSlides();
      if (!zoomableSlides.length) return '';
      if (requestedIndex < 0) return String(zoomableSlides[0].dataset.mtFpMediaId);

      let closestSlide = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      zoomableSlides.forEach((slide) => {
        const slideIndex = slides.indexOf(slide);
        const distance = Math.abs(slideIndex - requestedIndex);
        if (distance < closestDistance) {
          closestSlide = slide;
          closestDistance = distance;
        }
      });
      return closestSlide ? String(closestSlide.dataset.mtFpMediaId) : '';
    }

    setupThumbnailHydration() {
      const templates = Array.from(this.querySelectorAll('[data-mt-fp-thumbnail-template]'));
      if (!templates.length) return;
      const thumbnails = templates.map((template) => template.closest('.mt-fast-product__thumbnail')).filter(Boolean);
      if (!('IntersectionObserver' in window)) {
        thumbnails.forEach((thumbnail) => this.hydrateThumbnail(thumbnail));
        return;
      }
      this.thumbnailObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.hydrateThumbnail(entry.target);
          });
        },
        { root: this.querySelector('[data-mt-fp-thumbnail-scroller]'), rootMargin: '0px 160px' }
      );
      thumbnails.forEach((thumbnail) => this.thumbnailObserver.observe(thumbnail));
    }

    hydrateThumbnail(thumbnail) {
      const template = thumbnail?.querySelector('[data-mt-fp-thumbnail-template]');
      if (!template) return;
      thumbnail.querySelector('.mt-fast-product__thumbnail-shell')?.remove();
      thumbnail.append(template.content.cloneNode(true));
      template.remove();
      this.thumbnailObserver?.unobserve(thumbnail);
    }

    openMaterialDialog() {
      const selected = this.selectedOptions();
      const fields = this.optionFields();
      const materialIndex = fields.indexOf(this.fieldByRole('fabric'));
      const colorIndex = fields.indexOf(this.fieldByRole('color'));
      if (materialIndex < 0 || colorIndex < 0) return;
      const initialButton =
        this.findModalMaterialVisual(selected[materialIndex] || '', selected[colorIndex] || '') ||
        this.querySelector('[data-mt-fp-modal-visual]:not([disabled])');
      if (initialButton) this.selectModalMaterialVisual(initialButton);
      this.openDialog('material');
    }

    findModalMaterialVisual(fabricValue, colorValue) {
      return Array.from(this.querySelectorAll('[data-mt-fp-modal-visual]')).find(
        (button) =>
          button.dataset.mtFpModalFabricValue === fabricValue && button.dataset.mtFpModalColorValue === colorValue
      );
    }

    resolveModalMaterialVariant(fabricValue, colorValue) {
      const fields = this.optionFields();
      const selected = this.selectedOptions();
      const materialIndex = fields.indexOf(this.fieldByRole('fabric'));
      const colorIndex = fields.indexOf(this.fieldByRole('color'));
      if (materialIndex < 0 || colorIndex < 0) return null;
      return (
        this.variants.find(
          (variant) =>
            variant.options[materialIndex] === fabricValue &&
            variant.options[colorIndex] === colorValue &&
            variant.options.every(
              (value, index) => index === materialIndex || index === colorIndex || value === selected[index]
            )
        ) || null
      );
    }

    hydrateModalMaterialDetail(panel) {
      const template = panel?.querySelector('[data-mt-fp-modal-detail-template]');
      if (!template) return;
      template.parentElement?.append(template.content.cloneNode(true));
      template.remove();
    }

    setModalMaterialStatus(message, isError = false) {
      const status = this.querySelector('[data-mt-fp-modal-status]');
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      status.classList.toggle('is-error', Boolean(isError));
    }

    selectModalMaterialVisual(button, { focus = false, preserveStatus = false } = {}) {
      if (!button) return;
      const fabricValue = button.dataset.mtFpModalFabricValue || '';
      const colorValue = button.dataset.mtFpModalColorValue || '';
      const key = button.dataset.mtFpModalVisualKey || '';
      if (!fabricValue || !colorValue || !key) return;
      this.modalPreviewKey = JSON.stringify([fabricValue, colorValue]);
      this.querySelectorAll('[data-mt-fp-modal-visual]').forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-checked', String(active));
        candidate.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-mt-fp-modal-preview-panel]').forEach((panel) => {
        const active =
          panel.dataset.mtFpModalFabricValue === fabricValue && panel.dataset.mtFpModalColorValue === colorValue;
        panel.hidden = !active;
        if (active) this.hydrateModalMaterialDetail(panel);
      });
      const variant = this.resolveModalMaterialVariant(fabricValue, colorValue);
      const addButton = this.querySelector('[data-mt-fp-modal-add]');
      if (addButton) {
        const available = Boolean(variant?.available);
        addButton.dataset.mtFpModalVariantId = variant?.id || '';
        addButton.disabled = !available || this.cartRequestInFlight;
        addButton.querySelectorAll('[data-mt-fp-modal-add-text]').forEach((addButtonText) => {
          addButtonText.textContent = available
            ? addButton.dataset.mtFpModalAddLabel
            : addButton.dataset.mtFpModalSoldOutLabel;
        });
      }
      if (!preserveStatus) this.setModalMaterialStatus('');
      if (focus) button.focus();
    }

    selectPurchaseMaterialMap(button) {
      if (!button || button.disabled) return;
      const pairs = [
        { field: this.fieldByRole('fabric'), value: button.dataset.mtFpMapFabricValue || '' },
        { field: this.fieldByRole('color'), value: button.dataset.mtFpMapOptionValue || '' },
      ];
      let selectedAny = false;
      pairs.forEach((pair) => {
        const input = Array.from(pair.field?.querySelectorAll('[data-mt-fp-option]') || []).find(
          (candidate) => candidate.value === pair.value
        );
        if (input) {
          input.checked = true;
          selectedAny = true;
        }
      });
      if (selectedAny) this.syncVariant();
    }

    inheritedProductUrl(href) {
      const url = new URL(href, window.location.origin);
      const current = new URL(window.location.href);
      current.searchParams.forEach((value, key) => {
        if (
          key === 'view' ||
          key.startsWith('utm_') ||
          ['gclid', 'fbclid', 'twclid', 'ttclid', 'preview_theme_id', '_fd', 'pb'].includes(key)
        )
          url.searchParams.set(key, value);
      });
      if (current.hash) url.hash = current.hash;
      ['fabric', 'color'].forEach((role) => {
        const field = this.fieldByRole(role);
        const value = field?.querySelector('[data-mt-fp-option]:checked')?.value;
        if (value) url.searchParams.set(`mtfp_option_${role}`, value);
      });
      return url.toString();
    }

    resolveInheritedVariant() {
      const url = new URL(window.location.href);
      const inherited = [];
      url.searchParams.forEach((value, key) => {
        if (key.startsWith('mtfp_option_'))
          inherited.push({ name: key.slice('mtfp_option_'.length).toLowerCase(), value });
      });
      if (!inherited.length) return;
      const fields = this.optionFields();
      const indexes = inherited.map((item) => fields.indexOf(this.fieldByRole(item.name)));
      const inheritedCandidate = indexes.every((index) => index >= 0)
        ? this.variants.find(
            (variant) =>
              variant.available &&
              indexes.every((index, itemIndex) => variant.options[index] === inherited[itemIndex].value)
          )
        : null;
      const candidate = inheritedCandidate;
      if (candidate) {
        candidate.options.forEach((value, index) => {
          const input = Array.from(fields[index]?.querySelectorAll('[data-mt-fp-option]') || []).find(
            (node) => node.value === value
          );
          if (input) input.checked = true;
        });
        if (this.variantInput) this.variantInput.value = candidate.id;
      }
      Array.from(url.searchParams.keys())
        .filter((key) => key.startsWith('mtfp_option_'))
        .forEach((key) => url.searchParams.delete(key));
      window.history.replaceState(window.history.state, '', url.toString());
      if (candidate) this.syncVariant();
    }

    syncFamilySelection() {
      const currentProductId = String(this.dataset.mtFpCurrentProductId || '');
      if (!currentProductId) return;
      const modelLinks = Array.from(this.querySelectorAll('[data-mt-fp-model-product-ids]'));
      const hasServerModelSelection = modelLinks.some((link) => link.dataset.mtFpModelCurrent === 'true');
      modelLinks.forEach((link) => {
        const productIds = (link.dataset.mtFpModelProductIds || '').split(',').filter(Boolean);
        const current = hasServerModelSelection
          ? link.dataset.mtFpModelCurrent === 'true'
          : productIds.includes(currentProductId);
        link.classList.toggle('is-current', current);
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-mt-fp-size-product-id]').forEach((link) => {
        const current = String(link.dataset.mtFpSizeProductId || '') === currentProductId;
        link.classList.toggle('is-current', current);
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    async shareCurrentProduct(button) {
      const data = { title: document.title, url: window.location.href };
      if (navigator.share) {
        try {
          await navigator.share(data);
        } catch (_) {
          return;
        }
        return;
      }
      this.copyText(data.url, button);
    }

    getReviewSource() {
      const anchorId = this.dataset.reviewAnchorId;
      if (!anchorId) return null;

      const anchor = document.getElementById(anchorId);
      const anchorSection = anchor?.closest('.shopify-section');
      const appSection = anchorSection?.nextElementSibling;
      const app = appSection?.querySelector('#judgeme_product_reviews');
      if (!anchor || !anchorSection || !appSection || !app) return null;

      return { anchor, appSection, app };
    }

    isReviewPreviewOpen() {
      return Boolean(this.querySelector('[data-mt-fp-review-preview]:not([hidden])'));
    }

    openReviewPreview(trigger) {
      const preview = this.querySelector('[data-mt-fp-review-preview]');
      const slot = preview?.querySelector('[data-mt-fp-review-preview-slot]');
      const app = this.getReviewSource()?.app;
      if (!preview || !slot || !app) return false;

      this.reviewPreviewTrigger = trigger || this.reviewPreviewTrigger;
      preview.hidden = false;
      this.setAttribute('data-mt-fp-review-preview-open', '');
      this.syncMediaFilterState(null, { reviewActive: true });

      const resolved = this.refreshReviewPreview();
      this.reviewPreviewObserver?.disconnect();
      this.reviewPreviewObserver = null;
      window.clearTimeout(this.reviewPreviewFallbackTimer);
      this.reviewPreviewFallbackTimer = null;
      if (!resolved && 'MutationObserver' in window) {
        this.reviewPreviewObserver = new MutationObserver(() => {
          if (!this.isReviewPreviewOpen()) return;
          if (this.refreshReviewPreview()) {
            this.reviewPreviewObserver?.disconnect();
            this.reviewPreviewObserver = null;
            window.clearTimeout(this.reviewPreviewFallbackTimer);
            this.reviewPreviewFallbackTimer = null;
          }
        });
        this.reviewPreviewObserver.observe(app, {
          attributes: true,
          attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-score'],
          childList: true,
          subtree: true,
        });
        this.reviewPreviewFallbackTimer = window.setTimeout(() => {
          if (!this.isReviewPreviewOpen() || slot.querySelector('.mt-fast-product__review-card')) return;
          const status = document.createElement('p');
          status.className = 'mt-fast-product__review-preview-status';
          status.dataset.mtFpReviewPreviewStatus = '';
          status.textContent = this.dataset.reviewEmptyLabel || '';
          slot.replaceChildren(status);
        }, 4500);
      }
      return true;
    }

    closeReviewPreview({ restoreFocus = true } = {}) {
      const preview = this.querySelector('[data-mt-fp-review-preview]');
      if (!preview || preview.hidden) return;

      preview.hidden = true;
      this.removeAttribute('data-mt-fp-review-preview-open');
      this.reviewPreviewObserver?.disconnect();
      this.reviewPreviewObserver = null;
      window.clearTimeout(this.reviewPreviewFallbackTimer);
      this.reviewPreviewFallbackTimer = null;
      this.syncMediaFilterState(this.activeMediaGroup);
      const trigger = this.reviewPreviewTrigger;
      this.reviewPreviewTrigger = null;
      if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
    }

    refreshReviewPreview() {
      const preview = this.querySelector('[data-mt-fp-review-preview]');
      const slot = preview?.querySelector('[data-mt-fp-review-preview-slot]');
      const app = this.getReviewSource()?.app;
      if (!preview || !slot || !app) return false;

      const inlineReviewData = this.readReviewWidgetData(app);
      const summarySource = Array.from(app.querySelectorAll('[data-average-rating], [data-number-of-reviews]')).find(
        (node) => node.hasAttribute('data-average-rating') || node.hasAttribute('data-number-of-reviews')
      );
      const averageAttribute = summarySource?.getAttribute('data-average-rating');
      const countAttribute = summarySource?.getAttribute('data-number-of-reviews');
      const revampedSummary = app.querySelector('.jm-average-rating-display');
      const revampedSummaryText = revampedSummary?.querySelectorAll('.jm-text') || [];
      const revampedAverage = Number.parseFloat(revampedSummaryText[0]?.textContent || '');
      const revampedCount = Number.parseInt(revampedSummaryText[1]?.textContent || '', 10);
      const inlineAverage = Number(inlineReviewData?.average_rating);
      const inlineCount = Number(inlineReviewData?.number_of_reviews);
      const appAverage =
        averageAttribute === null || averageAttribute === undefined
          ? Number.isFinite(inlineAverage)
            ? inlineAverage
            : revampedAverage
          : Number(averageAttribute);
      const appCount =
        countAttribute === null || countAttribute === undefined
          ? Number.isFinite(inlineCount)
            ? inlineCount
            : revampedCount
          : Number(countAttribute);
      const fallbackAverage = Number(preview.dataset.reviewAverage);
      const fallbackCount = Number(preview.dataset.reviewCount);
      const average = Number.isFinite(appAverage) && appAverage > 0 ? appAverage : fallbackAverage;
      const count = Number.isFinite(appCount) && appCount >= 0 ? appCount : fallbackCount;
      const summary = preview.querySelector('[data-mt-fp-review-preview-summary]');
      const averageNode = preview.querySelector('[data-mt-fp-review-preview-average]');
      const stars = preview.querySelector('[data-mt-fp-review-preview-stars]');
      const countNode = preview.querySelector('[data-mt-fp-review-preview-count]');
      if (summary && (average > 0 || count > 0)) {
        const averageText = average.toFixed(1).replace(/\.0$/, '');
        summary.hidden = false;
        if (averageNode) averageNode.textContent = averageText;
        if (stars) {
          stars.style.setProperty('--mt-fp-review-score', String(Math.max(0, Math.min(5, average || 0))));
          stars.removeAttribute('aria-hidden');
          stars.setAttribute('role', 'img');
          stars.setAttribute(
            'aria-label',
            (preview.dataset.reviewRatingTemplate || '').replace('__rating__', averageText)
          );
        }
        if (countNode)
          countNode.textContent = (preview.dataset.reviewCountTemplate || '').replace('__count__', String(count || 0));
      }

      let reviews = Array.from(app.querySelectorAll('.jdgm-rev'))
        .map((node) => {
          const body = (node.querySelector('.jdgm-rev__body')?.textContent || '').replace(/\s+/g, ' ').trim();
          const title = (node.querySelector('.jdgm-rev__title')?.textContent || '').replace(/\s+/g, ' ').trim();
          const author = (node.querySelector('.jdgm-rev__author')?.textContent || '').replace(/\s+/g, ' ').trim();
          const rating = Number(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 0;
          return { author, body: body || title, rating };
        })
        .filter((review) => review.body)
        .slice(0, 2);

      if (!reviews.length) {
        reviews = Array.from(app.querySelectorAll('.jm-review-item'))
          .map((node) => {
            const body = (node.querySelector('.jm-review-content__body')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim();
            const title = (node.querySelector('.jm-review-content__title')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim();
            const productLabel = (node.querySelector('.jm-product-variant-card__product-link')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim();
            const author = (node.querySelector('.jm-reviewer-info__name')?.textContent || '')
              .replace(/\s+/g, ' ')
              .trim();
            const ratingLabel =
              node.querySelector('[aria-label*="out of 5 stars"]')?.getAttribute('aria-label') || '';
            const rating = Number.parseFloat(ratingLabel) || 0;
            return { author, body: body && body !== productLabel ? body : title, rating };
          })
          .filter((review) => review.body)
          .slice(0, 2);
      }

      if (!reviews.length && Array.isArray(inlineReviewData?.reviews)) {
        reviews = inlineReviewData.reviews
          .map((review) => {
            const bodyDocument = new DOMParser().parseFromString(String(review.body_html || ''), 'text/html');
            const body = (bodyDocument.body.textContent || '').replace(/\s+/g, ' ').trim();
            const title = String(review.title || '').replace(/\s+/g, ' ').trim();
            const author = String(review.reviewer_name || '').replace(/\s+/g, ' ').trim();
            return { author, body: body || title, rating: Number(review.rating) || 0 };
          })
          .filter((review) => review.body)
          .slice(0, 2);
      }

      if (reviews.length) {
        const fragment = document.createDocumentFragment();
        reviews.forEach((review) => fragment.append(this.createReviewPreviewCard(review, preview)));
        slot.replaceChildren(fragment);
        return true;
      }

      if (Number.isFinite(appCount) && appCount === 0) {
        const status = document.createElement('p');
        status.className = 'mt-fast-product__review-preview-status';
        status.dataset.mtFpReviewPreviewStatus = '';
        status.textContent = this.dataset.reviewEmptyLabel || '';
        slot.replaceChildren(status);
        return true;
      }
      return false;
    }

    readReviewWidgetData(app) {
      const script = Array.from(app.querySelectorAll('script:not([src])')).find((node) =>
        node.textContent.includes('jdgm.data.reviewWidget[')
      );
      if (!script) return null;
      const match = script.textContent.match(
        /jdgm\.data\.reviewWidget\[[^\]]+\]\s*=\s*(\{[\s\S]*\})\s*;?\s*$/
      );
      if (!match) return null;
      try {
        return JSON.parse(match[1]);
      } catch (error) {
        return null;
      }
    }

    createReviewPreviewCard(review, preview) {
      const card = document.createElement('article');
      card.className = 'mt-fast-product__review-card';
      const header = document.createElement('div');
      header.className = 'mt-fast-product__review-card-header';
      if (review.author) {
        const avatar = document.createElement('span');
        avatar.className = 'mt-fast-product__review-card-avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = review.author.slice(0, 1).toUpperCase();
        header.append(avatar);
        const author = document.createElement('span');
        author.className = 'mt-fast-product__review-card-author';
        author.textContent = review.author;
        header.append(author);
      }
      if (review.rating > 0) {
        const stars = document.createElement('span');
        stars.className = 'mt-fast-product__review-card-stars';
        stars.style.setProperty('--mt-fp-review-score', String(Math.max(0, Math.min(5, review.rating))));
        stars.setAttribute('role', 'img');
        stars.setAttribute(
          'aria-label',
          (preview.dataset.reviewRatingTemplate || '').replace('__rating__', String(review.rating))
        );
        stars.textContent = '★★★★★';
        header.append(stars);
      }
      if (header.childElementCount) card.append(header);
      const body = document.createElement('p');
      body.className = 'mt-fast-product__review-card-body';
      body.textContent = review.body;
      card.append(body);
      return card;
    }

    openDialog(name, { focusOrigin = document.activeElement, zoomMediaId = this.activeMediaId } = {}) {
      const dialog = this.querySelector(`[data-mt-fp-dialog="${name}"]`);
      if (!dialog) return false;
      if (dialog.open) return true;
      if (name === 'zoom') {
        const resolvedZoomMediaId = this.resolveZoomMediaId(zoomMediaId);
        if (!resolvedZoomMediaId) return false;
        const lightbox = this.querySelector('[data-mt-fp-lightbox]');
        if (typeof lightbox?.prepare !== 'function') {
          const openIntent = (this.zoomOpenIntent || 0) + 1;
          this.zoomOpenIntent = openIntent;
          customElements.whenDefined('mt-fast-product-lightbox').then(() => {
            if (!this.isConnected || this.zoomOpenIntent !== openIntent) return;
            this.openDialog(name, { focusOrigin, zoomMediaId: resolvedZoomMediaId });
          });
          return true;
        }
        if (!lightbox.prepare(resolvedZoomMediaId)) {
          return false;
        }
        this.zoomOpenIntent = (this.zoomOpenIntent || 0) + 1;
      } else {
        this.zoomOpenIntent = (this.zoomOpenIntent || 0) + 1;
      }
      this.dialogFocusOrigins.set(dialog, focusOrigin);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.classList.add('mt-fast-product__dialog--fallback');
      }
      this.updateGlobalDialogLock();
      dialog
        .querySelector(
          '[data-mt-fp-dialog-heading], .mt-fast-product__policy-dialog-header h2[tabindex="-1"], [data-mt-fp-close-dialog]'
        )
        ?.focus({ preventScroll: true });
      return true;
    }

    isPreviewableSpecificationFile(link) {
      if (!link) return false;
      try {
        const url = new URL(link.href, window.location.href);
        const isShopifyFile = url.hostname.endsWith('cdn.shopify.com') && url.pathname.includes('/files/');
        return url.protocol === 'https:' && (isShopifyFile || /\.pdf(?:$|[?#])/i.test(`${url.pathname}${url.search}`));
      } catch (_) {
        return false;
      }
    }

    openSpecificationFilePreview(trigger) {
      if (!this.isPreviewableSpecificationFile(trigger)) return false;
      const documentUrl = new URL(trigger.href, window.location.href).toString();
      const documentTitle = trigger.textContent.replace(/\s+/g, ' ').trim() || this.dataset.mtFpDocumentPreviewTitle || '';
      const viewer = window.open('', '_blank');
      if (!viewer) return false;
      viewer.opener = null;
      this.renderSpecificationFilePreviewShell(viewer, documentTitle, documentUrl);
      this.loadSpecificationFilePreview(viewer, documentUrl);
      return true;
    }

    renderSpecificationFilePreviewShell(viewer, documentTitle, documentUrl) {
      viewer.document.open();
      viewer.document.write('<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title></title><style>html,body{height:100%;margin:0}body{background:#f5f5f3;color:#1c1c1a;display:grid;font-family:Arial,sans-serif;grid-template-rows:auto minmax(0,1fr)}header{align-items:center;background:#fff;border-bottom:1px solid #e7e6e1;display:flex;gap:16px;justify-content:space-between;padding:16px 24px}h1{font-size:20px;line-height:1.2;margin:0;overflow-wrap:anywhere}main{align-items:center;display:flex;justify-content:center;min-height:0;padding:16px}.status{background:#fff;color:#4d4c47;margin:0;padding:32px;text-align:center}.fallback{color:#00586d;font-weight:600}</style></head><body><header><h1 data-mt-fp-preview-title></h1><a class="fallback" data-mt-fp-preview-external target="_blank" rel="noopener" hidden></a></header><main><p class="status" data-mt-fp-preview-status aria-live="polite"></p></main></body></html>');
      viewer.document.close();
      viewer.document.title = documentTitle;
      viewer.document.querySelector('[data-mt-fp-preview-title]').textContent = documentTitle;
      viewer.document.querySelector('[data-mt-fp-preview-status]').textContent = this.dataset.mtFpDocumentPreviewLoading || '';
      const externalLink = viewer.document.querySelector('[data-mt-fp-preview-external]');
      externalLink.href = documentUrl;
      externalLink.textContent = this.dataset.mtFpDocumentPreviewExternal || '';
    }

    async loadSpecificationFilePreview(viewer, documentUrl) {
      const controller = new AbortController();
      let objectUrl = null;
      let transferredToViewer = false;
      const cleanup = () => {
        controller.abort();
        if (objectUrl && !transferredToViewer) URL.revokeObjectURL(objectUrl);
      };
      viewer.addEventListener('pagehide', cleanup, { once: true });

      try {
        const response = await fetch(documentUrl, { signal: controller.signal });
        if (!response.ok) throw new Error(`Preview request failed with ${response.status}`);
        const content = await response.arrayBuffer();
        if (controller.signal.aborted || viewer.closed) return;
        const headerType = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() || '';
        const signature = new TextDecoder('ascii').decode(new Uint8Array(content, 0, Math.min(content.byteLength, 5)));
        const contentType = signature === '%PDF-' ? 'application/pdf' : headerType;
        if (!contentType || contentType === 'application/octet-stream') throw new Error('Unsupported preview type');
        objectUrl = URL.createObjectURL(new Blob([content], { type: contentType }));
        if (viewer.closed) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        this.specificationPreviewObjectUrls?.add(objectUrl);
        transferredToViewer = true;
        viewer.location.replace(objectUrl);
        this.releaseSpecificationPreviewWhenClosed(viewer, objectUrl);
      } catch (error) {
        if (controller.signal.aborted || viewer.closed) return;
        const status = viewer.document.querySelector('[data-mt-fp-preview-status]');
        const externalLink = viewer.document.querySelector('[data-mt-fp-preview-external]');
        if (status) status.textContent = this.dataset.mtFpDocumentPreviewUnavailable || '';
        if (externalLink) externalLink.hidden = false;
      }
    }

    releaseSpecificationPreviewWhenClosed(viewer, objectUrl) {
      const release = () => {
        URL.revokeObjectURL(objectUrl);
        this.specificationPreviewObjectUrls?.delete(objectUrl);
      };
      const poll = () => {
        if (viewer.closed) {
          release();
          return;
        }
        const timer = window.setTimeout(() => {
          this.specificationPreviewCleanupTimers?.delete(timer);
          poll();
        }, 1000);
        this.specificationPreviewCleanupTimers?.add(timer);
      };
      poll();
    }

    closeDialog(dialog, { restoreFocus = true } = {}) {
      if (!dialog?.hasAttribute('open')) return;
      const usesFallback = dialog.classList.contains('mt-fast-product__dialog--fallback');
      if (!usesFallback && typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        dialog.removeAttribute('aria-modal');
        dialog.classList.remove('mt-fast-product__dialog--fallback');
        this.onDialogClose(dialog, { restoreFocus });
      }
    }

    updateGlobalDialogLock() {
      document.body.classList.toggle(
        'mt-fast-product--dialog-open',
        Boolean(document.querySelector('mt-fast-product [data-mt-fp-dialog][open]'))
      );
    }

    onDialogClose(dialog, { restoreFocus = true } = {}) {
      this.updateGlobalDialogLock();
      if (dialog?.dataset.mtFpDialog === 'zoom') this.querySelector('[data-mt-fp-lightbox]')?.reset?.();
      const origin = this.dialogFocusOrigins.get(dialog);
      if (restoreFocus && origin?.isConnected) origin.focus?.();
      this.dialogFocusOrigins.delete(dialog);
    }

    async copyText(text, button) {
      const feedback = button.querySelector('[data-mt-fp-copy-feedback]');
      if (!feedback) return;
      window.clearTimeout(this.copyFeedbackTimer);
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const input = document.createElement('textarea');
          input.value = text;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.append(input);
          input.select();
          const copied = document.execCommand('copy');
          input.remove();
          if (!copied) throw new Error('Copy failed');
        }
        feedback.textContent = button.dataset.mtFpCopiedLabel || this.dataset.copiedLabel || '';
      } catch (_) {
        feedback.textContent =
          button.dataset.mtFpCopyErrorLabel || this.dataset.copyErrorLabel || this.dataset.errorLabel || '';
      }
      this.copyFeedbackTimer = window.setTimeout(() => {
        feedback.textContent = '';
      }, 1800);
    }

    async parseCartResponse(response) {
      const text = await response.text();
      if (!text) return {};
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('json')) return { description: '' };
      try {
        return JSON.parse(text);
      } catch (_) {
        return {};
      }
    }

    setCartMutationState(inFlight) {
      this.cartRequestInFlight = inFlight;
      this.querySelectorAll('[data-mt-fp-cart-action]').forEach((button) => {
        button.disabled = inFlight;
      });
      if (!inFlight) {
        if (this.currentVariant) this.setAvailability(Boolean(this.currentVariant.available));
        else this.setUnavailable();
        this.querySelectorAll('[data-mt-fp-accessory-carousel]').forEach((carousel) => this.setAccessoryVisibility(carousel));
        const selectedMaterial = this.querySelector('[data-mt-fp-modal-visual].is-active');
        if (selectedMaterial) this.selectModalMaterialVisual(selectedMaterial, { preserveStatus: true });
      }
      this.syncDynamicCheckoutAvailability();
    }

    async performCartAdd(body, { source, onSuccess, onError } = {}) {
      if (this.cartRequestInFlight) return false;
      this.clearActionStatuses();
      this.setCartMutationState(true);
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const payload = await this.parseCartResponse(response);
        if (!response.ok) {
          const serverMessage =
            typeof payload.description === 'string'
              ? payload.description
              : typeof payload.message === 'string'
              ? payload.message
              : '';
          throw new Error(serverMessage || this.dataset.errorLabel);
        }
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'product', origin: source || 'product' },
          })
        );
        onSuccess?.();
        return true;
      } catch (error) {
        onError?.(error instanceof Error && error.message ? error.message : this.dataset.errorLabel);
        return false;
      } finally {
        this.setCartMutationState(false);
      }
    }

    async onSubmit(event) {
      if (!event.submitter?.matches('[data-mt-fp-add]')) return;
      event.preventDefault();
      if (!this.form || !this.variantInput || !this.addButton || this.cartRequestInFlight) return;
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }
      await this.performCartAdd(new FormData(this.form), {
        source: 'product',
        onError: (message) => this.setStatus(message, true),
      });
    }

    async addAccessory(button) {
      if (!button?.dataset.mtFpAccessoryAdd || button.disabled || this.cartRequestInFlight) return;
      const input = button.closest('[data-mt-fp-accessory-slide]')?.querySelector('[data-mt-fp-accessory-quantity-input]');
      const quantity = this.normalizeAccessoryQuantity(input);
      const body = new FormData();
      body.append('id', button.dataset.mtFpAccessoryAdd);
      body.append('quantity', String(quantity));
      const status = button.closest('[data-mt-fp-accessory-carousel]')?.querySelector('[data-mt-fp-accessory-status]');
      const setAccessoryStatus = (message, isError) => {
        if (!status) return;
        status.textContent = message || '';
        status.hidden = !message;
        status.classList.toggle('is-error', Boolean(isError));
      };
      await this.performCartAdd(body, {
        source: 'product-accessory',
        onError: (message) => setAccessoryStatus(message, true),
      });
    }

    async addModalMaterial(button) {
      const variantId = button?.dataset.mtFpModalVariantId;
      if (!variantId || button.disabled || this.cartRequestInFlight) return;
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      await this.performCartAdd(body, {
        source: 'product-material-preview',
        onSuccess: () => this.closeDialog(button.closest('[data-mt-fp-dialog]'), { restoreFocus: false }),
        onError: (message) => this.setModalMaterialStatus(message, true),
      });
    }

    setStatus(message, isError) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
      this.status.classList.toggle('is-error', Boolean(isError));
    }

    setupStickyBar() {
      const sticky = this.querySelector('[data-mt-fp-sticky-bar]');
      if (!sticky) return;
      const addButton = this.querySelector('[data-mt-fp-add]');
      if (!addButton) return;
      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(
          ([entry]) =>
            sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0),
          { threshold: 0 }
        );
        this.stickyObserver.observe(addButton);
        return;
      }
      this.onStickyFallbackBound = () => {
        if (this.stickyFallbackFrame) return;
        this.stickyFallbackFrame = window.requestAnimationFrame(() => {
          this.stickyFallbackFrame = null;
          sticky.classList.toggle('is-visible', addButton.getBoundingClientRect().bottom < 0);
        });
      };
      window.addEventListener('scroll', this.onStickyFallbackBound, { passive: true });
      window.addEventListener('resize', this.onStickyFallbackBound, { passive: true });
      this.onStickyFallbackBound();
    }

    disconnectedCallback() {
      this.zoomOpenIntent = (this.zoomOpenIntent || 0) + 1;
      this.closeReviewPreview({ restoreFocus: false });
      this.reviewPreviewObserver?.disconnect();
      window.clearTimeout(this.reviewPreviewFallbackTimer);
      this.querySelectorAll('[data-mt-fp-dialog][open]').forEach((dialog) =>
        this.closeDialog(dialog, { restoreFocus: false })
      );
      this.informationPanelTimers?.forEach((timer) => window.clearTimeout(timer));
      this.informationPanelTimers?.clear();
      this.informationPanelLayoutAnimations?.forEach((animation) => animation.cancel());
      this.informationPanelLayoutAnimations?.clear();
      this.specificationPreviewCleanupTimers?.forEach((timer) => window.clearTimeout(timer));
      this.specificationPreviewCleanupTimers?.clear();
      this.specificationPreviewObjectUrls?.forEach((url) => URL.revokeObjectURL(url));
      this.specificationPreviewObjectUrls?.clear();
      this.querySelectorAll('[data-mt-fp-information-panel]').forEach((panel) => {
        delete panel.dataset.mtFpInformationTransition;
      });
      this.stickyObserver?.disconnect();
      this.thumbnailObserver?.disconnect();
      this.dynamicCheckoutObserver?.disconnect();
      if (this.mediaSyncFrame) window.cancelAnimationFrame(this.mediaSyncFrame);
      if (this.stickyFallbackFrame) window.cancelAnimationFrame(this.stickyFallbackFrame);
      window.clearTimeout(this.copyFeedbackTimer);
      if (this.onStickyFallbackBound) {
        window.removeEventListener('scroll', this.onStickyFallbackBound);
        window.removeEventListener('resize', this.onStickyFallbackBound);
      }
      this.removeEventListener('change', this.onChangeBound);
      this.removeEventListener('click', this.onClickBound);
      this.removeEventListener('keydown', this.onKeydownBound);
      this.form?.removeEventListener('submit', this.onSubmitBound);
      this.mediaTrack?.removeEventListener('scroll', this.onMediaScrollBound);
      this.zoomMobileQuery?.removeEventListener?.('change', this.onZoomViewportChangeBound);
      this.querySelectorAll('[data-mt-fp-dialog]').forEach((dialog) =>
        dialog.removeEventListener('close', this.onDialogCloseBound)
      );
      this.updateGlobalDialogLock();
      this.optionFieldsCache = null;
      this.informationPanelTimers = null;
      this.informationPanelLayoutAnimations = null;
      this.specificationPreviewCleanupTimers = null;
      this.specificationPreviewObjectUrls = null;
      this.initialized = false;
    }
  }

  if (!customElements.get('mt-fast-product')) customElements.define('mt-fast-product', MtFastProduct);
